/** @jsxImportSource theme-ui */
import * as React from 'react';
import { Link } from 'gatsby';
import { Box, Flex, Grid } from 'theme-ui';
import { useTranslation } from 'react-i18next';

import {
  useTrainingSessions,
  useUser,
  useVideoSessions,
} from '@youga/youga-client-api';
import type { UserPreferences, VideoSession } from '@youga/youga-interfaces';

import i18n from '../../../i18n/i18n';
import useTrainingAccess from '../../../hooks/useTrainingAccess';
import Text from '../../01_atoms/Text/Text';
import BtnPlay from '../../../assets/btnPlay.inline.svg';
import Button from '../../01_atoms/Button/Button';
import Select from '../../01_atoms/Select/Select';

const ACTIVE_COLOR = '#F48222';
const GOAL_OPTIONS = [
  'health_care',
  'twine',
  'improve_mobilitiy',
  'gear_up',
  'imprive_condition',
  'get_rid_of_tensions',
  'get_rid_of_stress',
  'relax',
  'be_young',
] as const;
const EXPERIENCE_LEVELS = [0, 1, 2, 3, 4] as const;
const FLEXIBILITY_LEVELS = ['no', 'little', 'yes'] as const;
type PersonalizedPlanSession = VideoSession & { completed: boolean };
type PlanActionPreferences = UserPreferences & {
  regenerateTrainingPlan?: boolean;
};

function PersonalizedTrainingPlan(): React.ReactElement | null {
  const { t } = useTranslation();
  const { data: user, updatePreferences } = useUser();
  const { data: videoSessionsData } = useVideoSessions(i18n.language);
  const { data: trainingSessions } = useTrainingSessions();
  const { isLocked, handleTrainingClick } = useTrainingAccess();
  const [isGeneratingPlan, setIsGeneratingPlan] = React.useState(false);
  const [showCriteriaEditor, setShowCriteriaEditor] = React.useState(false);
  const [planActionError, setPlanActionError] = React.useState(false);
  const [criteria, setCriteria] = React.useState<Partial<UserPreferences>>({});

  const plan = user?.stretchingPlan?.find(
    (candidate) => candidate.sessions?.length > 0,
  );

  const sessions = React.useMemo(() => {
    if (plan == null || videoSessionsData == null) {
      return [];
    }

    const planGeneratedAt = (plan as typeof plan & { generatedAt?: string })
      .generatedAt;
    const generatedAt = planGeneratedAt
      ? Date.parse(planGeneratedAt)
      : undefined;
    const completedSessionIds = new Set(
      trainingSessions
        ?.filter((session) => {
          if (generatedAt === undefined || Number.isNaN(generatedAt)) {
            return true;
          }
          const completedAt = Date.parse(session.timestamp);
          return !Number.isNaN(completedAt) && completedAt >= generatedAt;
        })
        .map((session) => session.videoSessionId),
    );

    return plan.sessions.reduce<PersonalizedPlanSession[]>(
      (result, planSession) => {
        const sessionId =
          planSession.videoSessionId ?? planSession.id ?? planSession.sessionId;
        const videoSession = videoSessionsData.videoSessions[sessionId];

        if (videoSession == null) {
          return result;
        }

        result.push({
          ...videoSession,
          completed:
            planSession.completed === true ||
            completedSessionIds.has(sessionId),
        });

        return result;
      },
      [],
    );
  }, [plan, trainingSessions, videoSessionsData]);

  if (plan == null || sessions.length === 0) {
    return null;
  }

  const firstIncompleteIndex = sessions.findIndex(
    (session) => !session.completed,
  );
  const allCompleted = firstIncompleteIndex === -1;

  const runPlanAction = async (preferences: PlanActionPreferences) => {
    try {
      setIsGeneratingPlan(true);
      setPlanActionError(false);
      await updatePreferences(preferences);
      setShowCriteriaEditor(false);
    } catch (_error: unknown) {
      setPlanActionError(true);
    } finally {
      setIsGeneratingPlan(false);
    }
  };

  const openCriteriaEditor = () => {
    setCriteria({
      goal: user?.preferences?.goal,
      experience: user?.preferences?.experience,
      stretched: user?.preferences?.stretched,
      howOften: user?.preferences?.howOften,
    });
    setPlanActionError(false);
    setShowCriteriaEditor(true);
  };

  return (
    <Box as="section" sx={{ mt: [4, 6], mb: [5, 8] }}>
      <Text variant="h2light" sx={{ fontSize: [3, 4], mb: 2 }}>
        {allCompleted
          ? t('personalized_plan.completed_title')
          : t('personalized_plan.title')}
      </Text>
      <Text variant="h6light" sx={{ maxWidth: '760px', mb: [3, 4] }}>
        {allCompleted
          ? t('personalized_plan.completed_subtitle')
          : t('personalized_plan.subtitle')}
      </Text>

      {allCompleted && (
        <Box
          sx={{
            maxWidth: '900px',
            backgroundColor: '#fff2eb',
            borderRadius: '14px',
            p: [3, 4],
            mb: 4,
          }}
        >
          <Text variant="h6" sx={{ mb: 3 }}>
            {t('personalized_plan.completed_question')}
          </Text>
          <Flex sx={{ gap: 3, flexWrap: 'wrap' }}>
            <Button
              variant="primary"
              disabled={isGeneratingPlan}
              onClick={() =>
                void runPlanAction({ regenerateTrainingPlan: true })
              }
            >
              {isGeneratingPlan
                ? t('personalized_plan.generating')
                : t('personalized_plan.continue_same')}
            </Button>
            <Button
              variant="whiteBorder"
              disabled={isGeneratingPlan}
              onClick={openCriteriaEditor}
            >
              {t('personalized_plan.change_criteria')}
            </Button>
          </Flex>

          {showCriteriaEditor && (
            <Box sx={{ mt: 4 }}>
              <Grid columns={[1, 2]} gap={3}>
                <label>
                  <Text sx={{ mb: 2 }}>
                    {t('personalized_plan.criteria.goal')}
                  </Text>
                  <Select
                    value={criteria.goal ?? ''}
                    onChange={(event) =>
                      setCriteria((current) => ({
                        ...current,
                        goal: event.target.value as UserPreferences['goal'],
                      }))
                    }
                  >
                    {GOAL_OPTIONS.map((goal) => (
                      <option key={goal} value={goal}>
                        {t(`GOAL_OPTIONS_LABELS.${goal}`)}
                      </option>
                    ))}
                  </Select>
                </label>
                <label>
                  <Text sx={{ mb: 2 }}>
                    {t('personalized_plan.criteria.experience')}
                  </Text>
                  <Select
                    value={criteria.experience ?? 2}
                    onChange={(event) =>
                      setCriteria((current) => ({
                        ...current,
                        experience: Number(event.target.value),
                      }))
                    }
                  >
                    {EXPERIENCE_LEVELS.map((level) => (
                      <option key={level} value={level}>
                        {t(`personalized_plan.criteria.experience_${level}`)}
                      </option>
                    ))}
                  </Select>
                </label>
                <label>
                  <Text sx={{ mb: 2 }}>
                    {t('personalized_plan.criteria.flexibility')}
                  </Text>
                  <Select
                    value={criteria.stretched ?? 'little'}
                    onChange={(event) =>
                      setCriteria((current) => ({
                        ...current,
                        stretched: event.target
                          .value as UserPreferences['stretched'],
                      }))
                    }
                  >
                    {FLEXIBILITY_LEVELS.map((level) => (
                      <option key={level} value={level}>
                        {t(`personalized_plan.criteria.flexibility_${level}`)}
                      </option>
                    ))}
                  </Select>
                </label>
                <label>
                  <Text sx={{ mb: 2 }}>
                    {t('personalized_plan.criteria.frequency')}
                  </Text>
                  <Select
                    value={criteria.howOften ?? '2_per_week'}
                    onChange={(event) =>
                      setCriteria((current) => ({
                        ...current,
                        howOften: event.target
                          .value as UserPreferences['howOften'],
                      }))
                    }
                  >
                    {[1, 2, 3, 4, 5].map((frequency) => (
                      <option key={frequency} value={`${frequency}_per_week`}>
                        {t('personalized_plan.criteria.frequency_value', {
                          count: frequency,
                        })}
                      </option>
                    ))}
                  </Select>
                </label>
              </Grid>
              <Button
                variant="primary"
                sx={{ mt: 3 }}
                disabled={isGeneratingPlan || criteria.goal == null}
                onClick={() => void runPlanAction(criteria as UserPreferences)}
              >
                {isGeneratingPlan
                  ? t('personalized_plan.generating')
                  : t('personalized_plan.save_criteria')}
              </Button>
            </Box>
          )}

          {planActionError && (
            <Text sx={{ mt: 3, color: 'error' }}>
              {t('personalized_plan.generation_error')}
            </Text>
          )}
        </Box>
      )}

      <Box sx={{ overflowX: 'auto', pb: 4, mx: ['-20px', 0] }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${sessions.length}, minmax(190px, 1fr))`,
            gap: 3,
            minWidth: `${Math.max(sessions.length * 210, 760)}px`,
            px: ['20px', 0],
          }}
        >
          {sessions.map((session, index) => {
            const isActive = index === firstIncompleteIndex;
            const stepColor = session.completed
              ? 'success'
              : isActive
              ? ACTIVE_COLOR
              : 'grey2';

            return (
              <Box key={`${session.id}-${index}`}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 3,
                  }}
                >
                  <Box
                    sx={{
                      width: '42px',
                      height: '42px',
                      flexShrink: 0,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: session.completed || isActive ? 'white' : 'grey4',
                      backgroundColor: stepColor,
                      border: session.completed || isActive ? 0 : '2px solid',
                      borderColor: stepColor,
                      fontFamily: 'heading',
                      fontWeight: 'bold',
                    }}
                    aria-label={
                      session.completed
                        ? t('personalized_plan.completed_step', {
                            number: index + 1,
                          })
                        : t('personalized_plan.step', { number: index + 1 })
                    }
                  >
                    {session.completed ? '✓' : index + 1}
                  </Box>
                  {index < sessions.length - 1 && (
                    <Box
                      sx={{
                        height: '2px',
                        flex: 1,
                        ml: 2,
                        backgroundColor: session.completed
                          ? 'success'
                          : 'grey1',
                      }}
                    />
                  )}
                </Box>

                <Box
                  sx={{
                    position: 'relative',
                    minHeight: '250px',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    border: isActive
                      ? `2px solid ${ACTIVE_COLOR}`
                      : '2px solid transparent',
                    boxShadow: isActive
                      ? '0 12px 28px rgba(244, 130, 34, 0.2)'
                      : '0 10px 20px rgba(0, 0, 0, 0.1)',
                    filter: isLocked ? 'grayscale(1)' : undefined,
                    opacity: isLocked ? 0.55 : session.completed ? 0.72 : 1,
                    transition:
                      'transform ease-in 0.15s, box-shadow ease-in 0.15s',
                    ':hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 14px 28px rgba(0, 0, 0, 0.16)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      height: '145px',
                      backgroundImage: `url(${
                        session.thumbnailSmallHq ||
                        session.thumbnailSmall ||
                        session.thumbnail
                      })`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background:
                          'linear-gradient(180deg, transparent 45%, rgba(0, 0, 0, 0.55) 100%)',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 2,
                        bottom: 2,
                        width: '34px',
                        height: '34px',
                      }}
                    >
                      <BtnPlay sx={{ width: '34px', height: '34px' }} />
                    </Box>
                    <Text
                      sx={{
                        position: 'absolute',
                        right: 3,
                        bottom: 3,
                        color: 'white',
                        fontSize: 0,
                      }}
                    >
                      {session.duration} min
                    </Text>
                  </Box>

                  <Box sx={{ p: 3 }}>
                    {isActive && (
                      <Text
                        sx={{
                          color: ACTIVE_COLOR,
                          fontSize: '11px',
                          fontWeight: 'bold',
                          textTransform: 'uppercase',
                          mb: 2,
                        }}
                      >
                        {t('personalized_plan.next')}
                      </Text>
                    )}
                    {session.completed && (
                      <Text
                        sx={{
                          color: 'grey4',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          textTransform: 'uppercase',
                          mb: 2,
                        }}
                      >
                        {t('personalized_plan.completed')}
                      </Text>
                    )}
                    <Text variant="h6" clampLines={2}>
                      {session.title}
                    </Text>
                  </Box>

                  <Link
                    to={`/app/session/${session.id}`}
                    onClick={handleTrainingClick}
                    aria-label={
                      isLocked
                        ? `${session.title} — subscription required`
                        : session.title
                    }
                    sx={{ position: 'absolute', inset: 0 }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

export default PersonalizedTrainingPlan;
